import {connect} from "react-redux";
import {RouteComponentProps, useHistory, useLocation} from "react-router";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {Breadcrumbs} from "@web2/breadcrumbs";
import {appLink} from "@web2/gh_routes";
import {PaginationWithList} from "@web2/pagination";

import {useRouteHandler} from "../../client/utils/use_route_handler";
import {VendorBox} from "../agency/components/VendorBox";
import {GridStatic} from "../app/atoms/Layout";
import {IStore} from "../app/reducers/hybrid_reducer";
import {appendQueryString} from "../app/utils/append_query_string";
import {parseSearch} from "../app/utils/request_response_utils/parse_search";
import {validateQuery} from "../app/utils/request_response_utils/validate_query";
import {Footer} from "../footer/Footer";
import {getMetaPage} from "../meta_data/utils/get_meta_page";
import {VendorType} from "../offer/detail/components/agency_and_developer/agency_and_developer_utils";
import {ArrowLeftIcon} from "../offer/list/components/icons/ArrowLeftIcon";
import {ArrowRightIcon} from "../offer/list/components/icons/ArrowRightIcon";
import {generateVendorBreadcrumbs} from "../offer/utils/generate_breadcrumbs";
import {IDevelopersListStore} from "./reducers/developers_list_reducer";

interface IStateProps {
    developersList: IDevelopersListStore;
}

const AGENCY_LISTING_SIZE = 36;

const DEVELOPERS_LOCATIONS = [
    {name: "Białystok", path: "bialystok"},
    {name: "Bydgoszcz", path: "bydgoszcz"},
    {name: "Elbląg", path: "elblag"},
    {name: "Gdańsk", path: "gdansk"},
    {name: "Gdynia", path: "gdynia"},
    {name: "Gliwice", path: "gliwice"},
    {name: "Katowice", path: "katowice"},
    {name: "Kielce", path: "kielce"},
    {name: "Kołobrzeg", path: "kolobrzeg"},
    {name: "Kraków", path: "krakow"},
    {name: "Legionowo", path: "legionowo"},
    {name: "Lublin", path: "lublin"},
    {name: "Łódź", path: "lodz"},
    {name: "Olsztyn", path: "olsztyn"},
    {name: "Poznań", path: "poznan"},
    {name: "Rzeszów", path: "rzeszow"},
    {name: "Sopot", path: "sopot"},
    {name: "Świnoujście", path: "swinoujscie"},
    {name: "Szczecin", path: "szczecin"},
    {name: "Toruń", path: "torun"},
    {name: "Warszawa", path: "warszawa"},
    {name: "Wrocław", path: "wroclaw"}
];

interface IDevelopersListDynamic extends IStateProps, RouteComponentProps {}
const DevelopersListDynamicC = (props: IDevelopersListDynamic) => {
    useRouteHandler();
    const location = useLocation();
    const history = useHistory();

    const {seoDescription, developersList} = props.developersList;
    const {total_offers, data} = developersList.results;
    const {page, count, next, previous, page_size} = developersList;
    const cityDeclinationWhere = (props.developersList.location && props.developersList.location.name_declension_where) || "";
    const pageNumber = getMetaPage(Number(page));
    const buildPageLink = (page: number) => {
        const query = validateQuery(["page"], parseSearch(location.search), page >= 2 ? {page: page.toString()} : {});
        return appendQueryString(location.pathname, query) + location.hash;
    };

    const onChangePage = (page: number) => {
        const query = validateQuery(["page"], parseSearch(location.search), page >= 2 ? {page: page.toString()} : {});
        history.push(appendQueryString(location.pathname, query) + location.hash);
    };
    const vendorBreadcrumbByLocation = !!props.developersList.location && {label: props.developersList.location.name, link: ""};

    return (
        <>
            <Breadcrumbs
                breadcrumbs={generateVendorBreadcrumbs(VendorType.DEVELOPER, vendorBreadcrumbByLocation ? [vendorBreadcrumbByLocation] : [])}
                variant="simple"
            />

            <GridStatic width="1185px" as="article">
                <DevelopersListHeader>
                    Lista deweloperów {props.developersList.location ? cityDeclinationWhere : "w Polsce"}
                    {pageNumber}
                </DevelopersListHeader>
                <p css={developersListSubHeader}>
                    Współpracujący z nami deweloperzy mają aktualnie w ofercie <strong>{total_offers}</strong> nieruchomości
                </p>
                <StyledDevelopersList>
                    {data.map((developer) => (
                        <VendorBox
                            key={developer.slug}
                            logo_picture={developer.logo_picture}
                            name={developer.name}
                            offer_summary={developer.offer_summary}
                            slug={developer.slug}
                            vendorType={VendorType.DEVELOPER}
                        />
                    ))}
                </StyledDevelopersList>
                <div css={paginationHolder}>
                    <PaginationWithList
                        onChangePageClick={onChangePage}
                        pageCount={Math.ceil(count / AGENCY_LISTING_SIZE)}
                        currentPage={page}
                        hrefBuilder={buildPageLink}
                        iconPrev={<ArrowLeftIcon />}
                        iconNext={<ArrowRightIcon />}
                        multiNumbersInside
                    />
                </div>
                <DevelopersListSubHeaderRegions>Lista deweloperów z podziałem na miasta</DevelopersListSubHeaderRegions>
                <DevelopersListRegionsHolder>
                    {DEVELOPERS_LOCATIONS.map((dev_location, index) => {
                        if (props.developersList.location && props.developersList.location.slug === dev_location.path) {
                            return;
                        }
                        const linkTitle = `Deweloper ${dev_location.name}`;

                        return (
                            <div key={dev_location.name + index}>
                                <a css={developersListLinkRegions} href={appLink.developer.location({location: dev_location.path})} title={linkTitle}>
                                    {linkTitle}
                                </a>
                            </div>
                        );
                    })}
                </DevelopersListRegionsHolder>
                {seoDescription && (
                    <SeoDescription>
                        <div dangerouslySetInnerHTML={{__html: seoDescription}} />
                    </SeoDescription>
                )}
            </GridStatic>
            <Footer />
        </>
    );
};
function mapStateToProps(state: IStore): IStateProps {
    return {
        developersList: state.developersList
    };
}

export const DevelopersListDynamic = connect(mapStateToProps)(DevelopersListDynamicC);

const StyledDevelopersList = styled.div`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 2rem;

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_md}) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
    }

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_lg}) {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
    }
`;
const paginationHolder = css`
    padding: 3rem 0;
`;

const DevelopersListHeader = styled.h1`
    font-size: 3.6rem;
    font-weight: 700;
    line-height: 120%;
    margin-top: 2.2rem;
    margin-bottom: 1.5rem;

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_lg}) {
        margin-top: 6.2rem;
`;

const DevelopersListSubHeaderRegions = styled.h2`
    font-size: 2rem;
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 3rem;

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_lg}) {
        margin-bottom: 3rem;
`;

const developersListSubHeader = css`
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 120%;
    margin-bottom: 5rem;
`;

const SeoDescription = styled.div`
    font-size: 1.4rem;
    font-weight: 400;
    letter-spacing: 0.0233rem;
    padding: 2.4rem;
    margin-bottom: 5rem;
    border: 0.01rem solid ${(props) => props.theme.colors.gray_very_bright};
    border-radius: ${(props) => props.theme.borders.border_radius_base};
`;

const DevelopersListRegionsHolder = styled.div`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    margin-bottom: 4.5rem;

    div:nth-child(4n) {
        margin-bottom: 3rem;
    }

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_md}) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        div:nth-child(4n) {
            margin-bottom: 0;
        }
    }

    @media screen and (min-width: ${(props) => props.theme.breakpoints.screen_lg}) {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        margin-bottom: 3rem;

        div:nth-child(4n) {
            margin-bottom: 0;
        }
    }
`;

const developersListLinkRegions = (theme: Theme) => css`
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 2.2rem;
    color: ${theme.colors.gray_lighter};

    &:hover,
    &:active,
    &:focus {
        cursor: pointer;
        color: ${theme.colors.gray_darker};
    }
`;
